<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        @click.self="handleCancel"
      >
        <div
          class="bg-white dark:bg-secondary-800 rounded-xl shadow-2xl max-w-md w-full transform transition-all"
          @click.stop
        >
          <!-- Header -->
          <div class="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
            <div class="flex items-center space-x-3">
              <div
                class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                :class="iconBgClass"
              >
                <component :is="iconComponent" :class="iconClass" class="w-5 h-5" />
              </div>
              <h3 class="text-lg font-semibold text-secondary-900 dark:text-white">
                {{ title }}
              </h3>
            </div>
          </div>

          <!-- Body -->
          <div class="px-6 py-4">
            <p class="text-secondary-600 dark:text-secondary-300">
              {{ message }}
            </p>
          </div>

          <!-- Footer -->
          <div class="px-6 py-4 bg-secondary-50 dark:bg-secondary-900/50 rounded-b-xl flex justify-end space-x-3">
            <button
              @click="handleCancel"
              class="px-4 py-2 text-sm font-medium text-secondary-700 dark:text-secondary-300 bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors"
            >
              {{ cancelText }}
            </button>
            <button
              @click="handleConfirm"
              class="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              :class="confirmButtonClass"
            >
              {{ confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { AlertTriangle, Trash2, Info, CheckCircle } from 'lucide-vue-next';

interface Props {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
}

const props = withDefaults(defineProps<Props>(), {
  confirmText: 'Confirmar',
  cancelText: 'Cancelar',
  variant: 'warning',
});

const emit = defineEmits<{
  confirm: [];
  cancel: [];
}>();

const iconComponent = computed(() => {
  switch (props.variant) {
    case 'danger':
      return Trash2;
    case 'warning':
      return AlertTriangle;
    case 'success':
      return CheckCircle;
    default:
      return Info;
  }
});

const iconBgClass = computed(() => {
  switch (props.variant) {
    case 'danger':
      return 'bg-red-100 dark:bg-red-900/30';
    case 'warning':
      return 'bg-yellow-100 dark:bg-yellow-900/30';
    case 'success':
      return 'bg-green-100 dark:bg-green-900/30';
    default:
      return 'bg-blue-100 dark:bg-blue-900/30';
  }
});

const iconClass = computed(() => {
  switch (props.variant) {
    case 'danger':
      return 'text-red-600 dark:text-red-400';
    case 'warning':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'success':
      return 'text-green-600 dark:text-green-400';
    default:
      return 'text-blue-600 dark:text-blue-400';
  }
});

const confirmButtonClass = computed(() => {
  switch (props.variant) {
    case 'danger':
      return 'bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700';
    case 'warning':
      return 'bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-600 dark:hover:bg-yellow-700';
    case 'success':
      return 'bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700';
    default:
      return 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700';
  }
});

function handleConfirm() {
  emit('confirm');
}

function handleCancel() {
  emit('cancel');
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.95);
  opacity: 0;
}
</style>
